'use client';

import { useTheme } from 'next-themes';
import { DriftTheme } from '../../environmentVariables/EnvironmentVariables';
import { MARKET_ICON_PATH } from 'src/constants/constants';
import { getBaseAssetSymbol } from '@velocity-exchange/common';
import { cm, cj } from 'src/utils/classNames';
import { CSSProperties, memo } from 'react';
import { PredictionMarketConfigs } from 'src/hooks/predictionMarkets/predictionMarketConfigs';
import { PredictionMarketIcon } from 'src/components/PredictionMarkets/PredictionMarketIcon';
import Image from 'next/image';
import { useIsolatedSpotSymbolToMainSpotSymbolMap } from 'src/hooks/spotMarkets/useIsolatedSpotSymbolToMainSpotMap';

const DEFAULT_SIZE = 16;

// Markets in here have base icons that have theme variants (e.g. apt-dark.svg, apt-light.svg)
const BASE_MARKET_SYMBOLS_WITH_THEME_VARIANTS_LIST = ['apt'];

// Markets in here use webp icons for all variants (i.e. base icon, grey dark/light icons)
const MARKET_SYMBOLS_USING_WEBP_LIST = ['wif', 'pepe'];

// Markets in here are a 1:1 mapping to their updated file name (e.g. sol_grey-dark: sol_grey-dark-v1.svg)
const ORIGINAL_FILE_NAME_TO_UPDATED_FILE_NAME_LOOKUP = {
	eth: 'eth-v1.svg',
	jlp: 'jlp-v1.svg',
	bonk: 'bonk.webp', // we want themed variants of bonks to still use svg
	inf: 'inf-v1.webp',
	cbbtc: 'cbbtc-v1.svg',
};

type MarketIconFileNameParams = {
	baseSymbol: string;
	grey?: boolean;
	theme?: DriftTheme;
};

const getMarketIconFileName = ({
	baseSymbol,
	grey,
	theme = DriftTheme.dark,
}: MarketIconFileNameParams) => {
	let originalFileName = `${baseSymbol.toLowerCase()}${
		grey ? `_grey-${theme}` : ''
	}`;

	if (
		BASE_MARKET_SYMBOLS_WITH_THEME_VARIANTS_LIST.includes(baseSymbol) &&
		!grey
	) {
		originalFileName = `${baseSymbol}-${theme}`;
	}

	if (
		ORIGINAL_FILE_NAME_TO_UPDATED_FILE_NAME_LOOKUP[
			originalFileName as keyof typeof ORIGINAL_FILE_NAME_TO_UPDATED_FILE_NAME_LOOKUP
		]
	) {
		return ORIGINAL_FILE_NAME_TO_UPDATED_FILE_NAME_LOOKUP[
			originalFileName as keyof typeof ORIGINAL_FILE_NAME_TO_UPDATED_FILE_NAME_LOOKUP
		];
	}

	if (MARKET_SYMBOLS_USING_WEBP_LIST.includes(baseSymbol.toLowerCase())) {
		return `${originalFileName}.webp`;
	}

	return `${originalFileName}.svg`;
};

export interface MarketIconSrcParams extends MarketIconFileNameParams {
	/**
	 * Whether to use `/api/market-icon` as the src.
	 *
	 * This is necessary for loading icons within the PnL modal. If our Next API route
	 * isn't used, the icons will not be included in the image generated for download.
	 */
	useNextApiRoute?: boolean;
}

export const getMarketIconSrc = ({
	baseSymbol,
	grey,
	theme = DriftTheme.dark,
	useNextApiRoute,
}: MarketIconSrcParams) => {
	const fileName = getMarketIconFileName({ baseSymbol, grey, theme });
	const src = useNextApiRoute
		? `/api/market-icon?path=${fileName}`
		: `${MARKET_ICON_PATH}/${fileName}`;

	return src;
};

interface TokenPairLogosProps extends Pick<
	MarketIconSrcParams,
	'useNextApiRoute'
> {
	fromTokenSymbol: string;
	toTokenSymbol: string;
	size?: number;
	leftImgFront?: boolean;
}

export const TokenPairLogos = ({
	fromTokenSymbol,
	toTokenSymbol,
	size,
	leftImgFront,
	useNextApiRoute,
}: TokenPairLogosProps) => {
	const theme = (useTheme().theme as DriftTheme) ?? DriftTheme.dark;

	if (!fromTokenSymbol || !toTokenSymbol) return null;

	const fromTokenLogo = getMarketIconSrc({
		baseSymbol: fromTokenSymbol,
		theme,
		grey: false,
		useNextApiRoute,
	});
	const toTokenLogo = getMarketIconSrc({
		baseSymbol: toTokenSymbol,
		theme,
		grey: false,
		useNextApiRoute,
	});

	return (
		<div className="-mr-sm flex shrink-0">
			<div>
				<img
					src={fromTokenLogo}
					className={cj('rounded-full', leftImgFront && 'relative z-10')}
					width={size ? size : DEFAULT_SIZE}
				/>
			</div>
			<div className="relative -left-[20%]">
				<img
					src={toTokenLogo}
					className="rounded-full"
					width={size ? size : DEFAULT_SIZE}
				/>
			</div>
		</div>
	);
};

interface MarketIconProps extends Pick<
	MarketIconSrcParams,
	'grey' | 'useNextApiRoute'
> {
	marketSymbol: string;
	className?: string;
	sizeClass?: string;
	customHeight?: number;
	customWidth?: number;
	isSpot?: boolean;
	style?: CSSProperties;
	pairSymbol?: string;
	useNextImageComponent?: boolean;
}

const MarketIcon = ({
	marketSymbol,
	className,
	grey,
	sizeClass: _sizeClass,
	customHeight,
	customWidth,
	isSpot,
	style,
	pairSymbol,
	useNextApiRoute,
	useNextImageComponent,
}: MarketIconProps) => {
	const isolatedSpotToMainSpotMarketSymbolMap =
		useIsolatedSpotSymbolToMainSpotSymbolMap();

	const hasCustomWidthAndHeight = customWidth && customHeight;

	const sizeClass =
		_sizeClass ??
		(hasCustomWidthAndHeight
			? `h-[${customHeight}px] w-[${customWidth}px]`
			: '');

	const overrideSymbol = isolatedSpotToMainSpotMarketSymbolMap[marketSymbol]; // Check if we should use an alternative/override marketSymbol in case the market is for an isolated spot market which may be for a "duplicate" asset

	const marketSymbolToUse = overrideSymbol ?? marketSymbol;

	const baseSymbol = getBaseAssetSymbol(marketSymbolToUse, true).toLowerCase();

	const theme = (useTheme().theme as DriftTheme) ?? DriftTheme.dark;

	if (!marketSymbol) {
		return null;
	}

	// displays as pair logo between base token and USDC token
	if (isSpot) {
		return (
			<TokenPairLogos
				fromTokenSymbol={baseSymbol}
				toTokenSymbol="usdc"
				size={customWidth ?? 14}
				leftImgFront
				useNextApiRoute={useNextApiRoute}
			/>
		);
	}

	// displays as pair logo between base token and pairSymbol token
	if (pairSymbol) {
		return (
			<TokenPairLogos
				fromTokenSymbol={baseSymbol}
				toTokenSymbol={pairSymbol}
				size={customWidth ?? 14}
				leftImgFront
				useNextApiRoute={useNextApiRoute}
			/>
		);
	}

	const src = getMarketIconSrc({
		baseSymbol,
		theme,
		grey,
		useNextApiRoute,
	});

	const predictionMarketConfig =
		PredictionMarketConfigs.getConfigFromMarketSymbol(marketSymbolToUse);

	if (predictionMarketConfig) {
		// apparently it needs both the classname and height/width to work for pngs and svgs
		return (
			<PredictionMarketIcon
				symbol={predictionMarketConfig.symbol}
				className={cm(sizeClass ?? 'h-[18px] w-[18px]', className)}
				small
			/>
		);
	}

	// Apparently it needs both the classname and height/width to work for pngs and svgs
	if (useNextImageComponent) {
		return (
			<Image
				src={src}
				className={cm(sizeClass ?? 'h-[18px] w-[18px]', className)}
				width={customWidth ?? 18}
				height={customHeight ?? 18}
				alt={`${marketSymbol} icon`}
				style={style}
			/>
		);
	}
	return (
		<img
			src={src}
			className={cm(sizeClass ?? 'h-[18px] w-[18px]', className)}
			width={customWidth ?? 18}
			height={customHeight ?? 18}
			alt={`${marketSymbolToUse} icon`}
			style={style}
		/>
	);
};

export default memo(MarketIcon);
